import useAxios from "axios-hooks";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UsersResDto } from "../../api/Api";
import AutocompleteInput from "../../components/input/AutoCompleteInput";
import PaginationButtons from "../../components/pagination/PaginationButtons";

export default function AssignmentStudents() {
  const navigate = useNavigate();
  const params = useParams();

  const [searchString, setSearchString] = useState(undefined as any);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [{ data, loading, error }, getStudents] = useAxios<UsersResDto>({
    url: `/assignments/${params.assignmentId}/students`,
    params: {
      page,
      limit: pageSize,
      search: searchString,
    },
  });


  useEffect(() => {}, [searchString]);

  return (
    <div className="flex flex-col gap-2 ">
      <div className="flex">
        <div className="mb-2">
          <AutocompleteInput
            onComplete={(input: string) => {
              setSearchString(input);
            }}
            placeHolder="Search..."
            className="bg-base-200"
          ></AutocompleteInput>
        </div>
      </div>
      {!loading ? (
        <div className="flex gap-4 flex-col">
          <AssignmentsStudentsTable
            data={data?.docs}
          ></AssignmentsStudentsTable>
          <PaginationButtons
            page={page}
            setPage={setPage}
            pageCount={data?.totalPages || 1}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      ) : (
        <div className="h-[20vh] w-full flex items-center justify-center">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}
    </div>
  );
}

export function AssignmentsStudentsTable({ data }: { data: any }) {
  const [selected, setSelected] = useState<any>(null);

  const closeModal = () => setSelected(null);

  return (
    <div className="overflow-x-auto rounded-xl border border-base-300 bg-base-100 table-container">
      <table className="table table-md w-full">
        <thead className="bg-base-200 text-base-content sticky top-0 z-10">
          <tr>
            <th className="text-sm">Student</th>
            <th className="text-sm">Email</th>
            <th className="text-sm">Classroom</th>
            <th className="text-sm">Courses</th>
            <th className="text-sm">Labs</th>
            <th className="text-sm">Progress</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((student: any) => {
            const {
              _id,
              user,
              classroom,
              totalCoursesCount,
              finishedCoursesCount,
              totalLabsCount,
              finishedLabsCount,
              progress,
            } = student;

            const isComplete = progress === 100;

            return (
              <tr
                key={_id}
                onClick={() => setSelected(student)}
                className={`hover:bg-base-300 transition cursor-pointer 
                  
                `}
              >
                <td className="text-sm font-medium">{user?.username}</td>
                <td className="text-sm">{user?.email}</td>
                <td className="text-sm">{classroom?.name}</td>
                <td className="text-sm">
                  {finishedCoursesCount} / {totalCoursesCount}
                </td>
                <td className="text-sm">
                  {finishedLabsCount} / {totalLabsCount}
                </td>
                <td className="text-sm w-[160px]">
                  {isComplete ? (
                    <div className="flex items-center gap-1 text-success">
                      <CheckCircleIcon className="w-5 h-5" />
                      <span className="text-xs font-medium">Completed</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <progress
                        className="progress progress-primary w-full"
                        value={progress}
                        max={100}
                      />
                      <span className="text-xs">{progress}%</span>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {selected && (
        <dialog id="details_modal" className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="font-bold text-lg mb-2">
              {selected.user?.username}'s Progress
            </h3>

            <div className="space-y-2">
              <div className="text-sm">
                <strong>Email:</strong> {selected.user?.email}
              </div>
              <div className="text-sm">
                <strong>Classroom:</strong> {selected.classroom?.name}
              </div>

              <div className="mt-4">
                <h4 className="font-semibold mb-1">Courses</h4>
                {selected.coursesProgress?.length > 0 ||
                selected?.unfinishedCourses.length > 0 ? (
                  <ul className="space-y-1">
                    {selected.coursesProgress.map((course: any) => (
                      <li
                        key={course._id}
                        className="flex justify-between items-center text-sm border-b dark:border-white/30 px-3 py-2"
                      >
                        <span>{course.courseName}</span>
                        {course.finished ? (
                          <span className="text-success flex items-center gap-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            Finished
                          </span>
                        ) : (
                          <span className="text-warning flex items-center gap-1">
                            <XMarkIcon className="w-4 h-4" />
                            In Progress
                          </span>
                        )}
                      </li>
                    ))}
                    {selected?.unfinishedCourses.map((lab: any) => (
                      <li
                        key={lab._id}
                        className="flex justify-between items-center text-sm border-b dark:border-white/30  px-3 py-2"
                      >
                        <span>{lab.name}</span>
                        <span className="text-error flex items-center gap-1">
                          <XMarkIcon className="w-4 h-4" />
                          Yet to Begin
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm italic text-base-content/60 px-3 py-2 border-b dark:border-white/30 ">
                    No course progress available.
                  </div>
                )}
              </div>

              <div className="mt-4">
                <h4 className="font-semibold mb-1">Labs</h4>
                {selected.labsProgress?.length > 0 ||
                selected?.unfinishedLabs.length > 0 ? (
                  <ul className="space-y-1">
                    {selected.labsProgress.map((lab: any) => (
                      <li
                        key={lab._id}
                        className="flex justify-between items-center text-sm border-b dark:border-white/30  px-3 py-2"
                      >
                        <span>{lab.labName}</span>
                        {lab.finished ? (
                          <span className="text-success flex items-center gap-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            Finished
                          </span>
                        ) : (
                          <span className="text-warning flex items-center gap-1">
                            <XMarkIcon className="w-4 h-4" />
                            In Progress
                          </span>
                        )}
                      </li>
                    ))}
                    {selected?.unfinishedLabs.map((lab: any) => (
                      <li
                        key={lab._id}
                        className="flex justify-between items-center text-sm border-b  dark:border-white/30 px-3 py-2"
                      >
                        <span>{lab.name}</span>
                        <span className="text-error flex items-center gap-1">
                          <XMarkIcon className="w-4 h-4" />
                          Yet to Begin
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm italic text-base-content/60 px-3 py-2 border  dark:border-white/30 rounded">
                    No lab progress available.
                  </div>
                )}
              </div>

              <div className="mt-4">
                <h4 className="font-semibold mb-1">Overall Progress</h4>
                <progress
                  className="progress progress-primary w-full"
                  value={selected.progress}
                  max={100}
                />
                <div className="text-sm text-center mt-1">
                  {selected.progress}% Complete
                </div>
              </div>
            </div>

            <div className="modal-action">
              <button className="btn btn-sm" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
}
